import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface SnackbarProps {
  open: boolean;
  message: string;
  onClose: () => void;
  duration?: number;
  type?: 'success' | 'error' | 'info';
}

function Snackbar({ open, message, onClose, duration = 3000, type = 'success' }: SnackbarProps) {
  useEffect(() => {
    if (open && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [open, duration, onClose]);

  if (!open) return null;

  const bgColor =
    type === 'success' ? 'bg-green-600' : type === 'error' ? 'bg-red-600' : 'bg-blue-600';

  const snackbar = (
    <div
      data-testid="snackbar"
      className="animate-slide-up fixed bottom-4 left-1/2 z-50 -translate-x-1/2 transform"
    >
      <div
        className={`${bgColor} flex max-w-[500px] min-w-[300px] items-center gap-3 rounded-lg px-6 py-3 text-white shadow-lg`}
      >
        {type === 'success' && (
          <svg className="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
        {type === 'error' && (
          <svg className="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
        <p className="flex-1 text-sm font-medium">{message}</p>
        <button
          onClick={onClose}
          className="shrink-0 text-white transition-colors hover:text-gray-200"
          aria-label="Close"
        >
          <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );

  return createPortal(snackbar, document.body);
}

export { Snackbar };
